import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import FormField from '@/shared/components/forms/WaveUIForm_V2/types/formField.type';
import Metadata from '@/shared/types/metadata/index.type';
import MetadataDefinitions from '@/shared/types/metadata/metadataDefinitions.type';

import filterInventoryMetadata from '../utils/filterInventoryMetadata';
import getMetadataForm from '../utils/getMetadataForm';
import metadataFieldChangeHandler from '../utils/metadataFieldChangeHandler';
import metadataFormFieldVisibilityChangeHandler from '../utils/metadataFormFieldVisibilityChangeHandler';
import useMetadataFormValidation from './useMetadataFormValidation';

type Args = {
  metadata: Metadata;
  constraint: 'ATOM' | 'INVENTORY';
  initialData: Record<string, unknown> | null;
};

const useInventoryMetadataForms = ({
  metadata,
  constraint,
  initialData,
}: Args) => {
  const initialDataRef = useRef<Record<string, unknown> | null>(null);

  const [requiredFormsList, setRequiredFormsList] = useState<FormField[][]>([]);
  const [optionalFormsList, setOptionalFormsList] = useState<FormField[][]>([]);
  const [optionalDefinitions, setOptionalDefinitions] = useState<
    MetadataDefinitions[]
  >([]);
  const [itemsQuantity, setItemsQuantity] = useState<number | null>(null);

  const { updateFormFieldsErrors } = useMetadataFormValidation();

  useEffect(() => {
    const optionalMetadata = filterInventoryMetadata({
      metadataDefinitions: metadata.metadata_definitions,
      levelConstraint: constraint,
      requiredLevel: 'OPTIONAL',
    });

    const requiredMetadata = filterInventoryMetadata({
      metadataDefinitions: metadata.metadata_definitions,
      levelConstraint: constraint,
      requiredLevel: 'REQUIRED',
    });

    setOptionalDefinitions(optionalMetadata);

    if (!isEqual(initialDataRef.current, initialData)) {
      initialDataRef.current = initialData;

      setRequiredFormsList(
        getMetadataForm({
          metadataDefinitions: requiredMetadata,
          initialData,
        }) as FormField[][],
      );

      setOptionalFormsList(
        getMetadataForm({
          metadataDefinitions: optionalMetadata,
          initialData,
        }) as FormField[][],
      );
    }
  }, [metadata, constraint, initialData]);

  const handleFieldValuesChange = (
    fieldValue: Record<string, unknown>,
    source: 'required' | 'optional',
    index: number,
  ) => {
    source === 'required'
      ? setRequiredFormsList((prev) =>
          metadataFieldChangeHandler(fieldValue, prev, index),
        )
      : setOptionalFormsList((prev) =>
          metadataFieldChangeHandler(fieldValue, prev, index),
        );
  };

  const handleItemsQuantityChange = (quantity: number) => {
    setItemsQuantity(quantity);
  };

  const handleFieldVisibilityChange = (fields: string[]) => {
    setOptionalFormsList((prev) =>
      metadataFormFieldVisibilityChangeHandler(fields, prev),
    );
  };

  const handleValidationErrors = (
    errors: {
      field_name: string;
      error: string;
    }[],
  ) => {
    setRequiredFormsList((prev) => updateFormFieldsErrors(errors, prev));
  };

  return {
    requiredFormsList,
    optionalFormsList,
    optionalMetadata: optionalDefinitions,
    itemsQuantity,
    handleFieldValuesChange,
    handleFieldVisibilityChange,
    handleItemsQuantityChange,
    handleValidationErrors,
  };
};

export default useInventoryMetadataForms;
